import { useCallback, useEffect, useMemo, useState } from "react"
import {
  Package,
  ShoppingCart,
  Users,
  AlertTriangle,
  Wallet,
  Plus,
  CreditCard,
  Sparkles,
  CarFront,
  Building2,
  Gauge,
  BedDouble,
  MapPin,
  CalendarDays,
  ArrowUpRight,
  Boxes,
  BadgeCent,
} from "lucide-react"
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts"

import { getDashboardStatData, getGeneralDashboardStatData } from "../../store/actions"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import SubscriptionModal from "../../components/subscrptions/SubscriptionModal"
import { runSubscriptionUpgrade } from "../../utils/subscriptionUpgrade"
import { getCurrentPlan } from "../../utils/permissions"
import { DASHBOARD_REFRESH_EVENT } from "../../utils/dashboardRefresh"
import { isCatalogStore, isCarStore, isPropertyStore } from "../../utils/storeCategory"
import { getStoreExperience } from "../../utils/storeExperience"
import { getGeneralProductsCatalog } from "../../api/products/get"

type LiveCatalogItem = {
  _id?: string
  name?: string
  identifier?: string
  coverImageUrl?: string
  imageUrl?: string
  sellPrice?: number
  buyPrice?: number
  dailyRentalPrice?: number
  monthlyRentalPrice?: number
  availability?: string
  status?: string
  district?: string
  location?: string
  propertyType?: string
  listingType?: string
  year?: string
  mileage?: string
  fuelType?: string
  brand?: string
  quantity?: number
}

export default function Dashboard() {
  const { userdata, dashboardstatdata, generaldashboardstatdata } = useSelector((state: any) => state.UserReducer)
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const { user, store } = userdata
  const currentPlan = getCurrentPlan(userdata)
  const availablePlans = userdata?.availablePlans
  const currentBillingCycle = userdata?.subscription?.billingCycle || userdata?.user?.subscriptionBillingCycle || "monthly"
  const catalogStore = isCatalogStore(store)
  const carStore = isCarStore(store)
  const propertyStore = isPropertyStore(store)
  const experience = getStoreExperience(store)
  const generalStore = experience.kind === "general"
  const serviceStore = experience.kind === "service"
  const fashionStore = experience.kind === "fashion"

  const dashboardData = catalogStore ? generaldashboardstatdata : dashboardstatdata
  const stats = dashboardData?.stats || {}
  const debtors = Array.isArray(dashboardData?.debtors) ? dashboardData.debtors : []
  const chartData = Array.isArray(dashboardData?.charts?.last7DaysSales)
    ? dashboardData.charts.last7DaysSales
    : []

  const [openSubscriptionModal, setOpenSubscriptionModal] = useState(false)
  const [liveCatalog, setLiveCatalog] = useState<LiveCatalogItem[]>([])

  const produitsEnStock = stats.totalInStock || 0
  const produitsVendus = stats.totalSold || 0
  const totalEncaisse = stats.totalPaid || 0
  const totalDettes = stats.totalDebt || 0
  const totalClients = stats.totalClients || 0
  const totalPaidToday = stats.totalPaidToday || 0

  const DETTE_ALERT_SEUIL = 200_000
  const showDebtAlert = totalDettes > DETTE_ALERT_SEUIL

  const last7DaysSales = chartData.map((d: any) => ({
    day: d?._id?.day || d?.day || "-",
    amount: Number(d?.amount || 0),
  }))

  const previewItems = useMemo(() => liveCatalog.slice(0, 3), [liveCatalog])
  const chartSpanClass = carStore || propertyStore || serviceStore ? "xl:col-span-8" : "xl:col-span-7"
  const sideSpanClass = carStore || propertyStore || serviceStore ? "xl:col-span-4" : "xl:col-span-5"
  const averageTicket = produitsVendus > 0 ? Math.round(totalEncaisse / produitsVendus) : 0
  const debtRatio = totalEncaisse > 0 ? Math.round((totalDettes / totalEncaisse) * 100) : 0
  const visibleCatalogValue = previewItems.reduce((sum, item) => sum + Number(item.sellPrice || 0), 0)

  const dashboardCopy = useMemo(() => {
    if (propertyStore) {
      return {
        heroBadge: "Property Flow",
        title: "Tableau de bord immobilier",
        subtitle: "Suivez les encaissements, les dossiers actifs et les biens en vitrine depuis une vue agence plus premium.",
        actionLabel: "Nouvelle transaction",
        collectionTitle: "Biens en direct",
        collectionSubtitle: "Un apercu vivant des biens actuellement visibles dans votre portefeuille.",
        chartTitle: "Tendance ventes & locations",
        chartBadge: "Live portefeuille",
        debtTitle: "Encours clients",
        debtBadge: "Relances",
      }
    }

    if (carStore) {
      return {
        heroBadge: "Garage Control",
        title: "Tableau de bord auto",
        subtitle: "Gardez un oeil sur le parc, les encaissements et les vehicules a forte rotation depuis une vue showroom.",
        actionLabel: "Nouvelle transaction",
        collectionTitle: "Vehicules en direct",
        collectionSubtitle: "Les fiches vehicules les plus recentes pour piloter le parc sans quitter le dashboard.",
        chartTitle: "Tendance ventes & locations",
        chartBadge: "Live parc auto",
        debtTitle: "Dossiers clients",
        debtBadge: "Suivi",
      }
    }

    if (serviceStore) {
      return {
        heroBadge: "Service Studio",
        title: "Tableau de bord services",
        subtitle: "Suivez vos prestations publiees, les demandes clients et les encaissements sans perdre la structure de votre catalogue.",
        actionLabel: "Nouveau devis",
        collectionTitle: "Services en direct",
        collectionSubtitle: "Un apercu rapide des prestations visibles pour vos prospects sur le site service.",
        chartTitle: "Tendance devis & ventes",
        chartBadge: "Live services",
        debtTitle: "Relances clients",
        debtBadge: "A traiter",
      }
    }

    if (fashionStore) {
      return {
        heroBadge: "Mode Studio",
        title: "Tableau de bord Mode",
        subtitle: "Suivez votre collection, vos tailles et pointures en rupture et les pieces qui se vendent le plus vite.",
        actionLabel: "Nouvelle vente",
        collectionTitle: "Nouveautés en direct",
        collectionSubtitle: "Les derniers articles ajoutes a votre collection pour garder le cap sur les tendances.",
        chartTitle: "Tendance des ventes (7 jours)",
        chartBadge: "Live collection",
        debtTitle: "Relances clientes",
        debtBadge: "A traiter",
        kpiStock: "Articles en collection",
        kpiSold: "Pieces vendues",
        kpiCash: "Encaissement total",
      }
    }

    return {
      heroBadge: "Boutique Control",
      title: "Centre de pilotage boutique",
      subtitle: "Une vue plus premium de votre stock, de vos ventes et des encaissements pour garder le rythme du magasin.",
      actionLabel: "Nouvelle vente",
      collectionTitle: "Vitrine produits",
      collectionSubtitle: "Mettez en avant quelques references recentes pour garder un oeil sur le catalogue actif.",
      chartTitle: "Tendance des ventes (7 jours)",
      chartBadge: "Live KPI",
      debtTitle: "Debiteurs recents",
      debtBadge: "A suivre",
    }
  }, [carStore, propertyStore, serviceStore, fashionStore])

  const kpiLabels = useMemo(() => {
    if (propertyStore) {
      return {
        stock: "Biens actifs",
        sold: "Dossiers conclus",
        clients: "Contacts",
        cash: "Encaissement total",
      }
    }

    if (carStore) {
      return {
        stock: "Vehicules en parc",
        sold: "Transactions auto",
        clients: "Prospects",
        cash: "Encaissement total",
      }
    }

    if (serviceStore) {
      return {
        stock: "Services actifs",
        sold: "Demandes conclues",
        clients: "Prospects",
        cash: "Encaissement total",
      }
    }

    return {
      stock: catalogStore ? "Articles en stock" : "Produits disponibles",
      sold: catalogStore ? "Ventes realisees" : "Produits vendus",
      clients: "Clients",
      cash: "Encaissement total",
    }
  }, [carStore, propertyStore, catalogStore, serviceStore])

  const refreshDashboard = useCallback(() => {
    if (!store?._id) return

    if (catalogStore) {
      dispatch(getGeneralDashboardStatData(store._id))
    } else {
      dispatch(getDashboardStatData(store._id))
    }
  }, [catalogStore, dispatch, store?._id])

  const refreshLiveCatalog = useCallback(async () => {
    if (!store?._id || !catalogStore) {
      setLiveCatalog([])
      return
    }

    try {
      const data = await getGeneralProductsCatalog(store._id, "")
      const items = Array.isArray(data) ? data : []
      setLiveCatalog(items)
    } catch (error) {
      console.error(error)
      setLiveCatalog([])
    }
  }, [catalogStore, store?._id])

  useEffect(() => {
    if (!store?._id) return
    refreshDashboard()
    refreshLiveCatalog()
  }, [refreshDashboard, refreshLiveCatalog, store?._id])

  useEffect(() => {
    const handleDashboardRefresh = () => {
      if (!store?._id) return
      refreshDashboard()
      refreshLiveCatalog()
    }

    window.addEventListener(DASHBOARD_REFRESH_EVENT, handleDashboardRefresh)
    return () => window.removeEventListener(DASHBOARD_REFRESH_EVENT, handleDashboardRefresh)
  }, [refreshDashboard, refreshLiveCatalog, store?._id])

  return (
    <>
      <div className="space-y-8">
        <section className={`relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br p-6 sm:p-7 text-white shadow-xl ${experience.stock.heroClass}`}>
          <div className={`pointer-events-none absolute -top-14 -right-10 h-36 w-36 rounded-full blur-3xl ${experience.stock.heroGlowClass}`} />
          <div className="pointer-events-none absolute -bottom-12 -left-8 h-32 w-32 rounded-full bg-white/10 blur-3xl" />

          <div className="relative flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <p className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2.5 py-1 text-[11px] uppercase tracking-[0.16em]">
                <Sparkles size={12} /> {dashboardCopy.heroBadge}
              </p>
              <h1 className="mt-3 text-2xl sm:text-3xl font-bold">{dashboardCopy.title}</h1>
              <p className="mt-2 text-sm text-white/85 max-w-2xl">{dashboardCopy.subtitle}</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="rounded-2xl border border-white/25 bg-white/10 px-4 py-3 backdrop-blur-sm">
                <p className="text-xs uppercase tracking-[0.12em] text-white/75">Encaissement du jour</p>
                <p className="mt-1 text-xl font-semibold">{Number(totalPaidToday || 0).toLocaleString()} FCFA</p>
              </div>
              <div className="rounded-2xl border border-white/25 bg-white/10 px-4 py-3 backdrop-blur-sm">
                <p className="text-xs uppercase tracking-[0.12em] text-white/75">Plan actif</p>
                <p className="mt-1 text-xl font-semibold">{currentPlan}</p>
              </div>
            </div>
          </div>

          {generalStore && (
            <div className="relative mt-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <HeroMiniStat
                label="Articles actifs"
                value={produitsEnStock}
                hint={`${liveCatalog.length} ref. en vitrine`}
                icon={<Boxes size={16} />}
              />
              <HeroMiniStat
                label="Ticket moyen"
                value={`${averageTicket.toLocaleString()} FCFA`}
                hint={produitsVendus > 0 ? `${produitsVendus} vente${produitsVendus > 1 ? "s" : ""}` : "Aucune vente"}
                icon={<BadgeCent size={16} />}
              />
              <HeroMiniStat
                label="Pression dettes"
                value={`${debtRatio}%`}
                hint={totalDettes > 0 ? `${totalDettes.toLocaleString()} FCFA a suivre` : "Aucun encours"}
                icon={<ArrowUpRight size={16} />}
              />
            </div>
          )}

          <div className="relative mt-5 flex flex-wrap gap-2.5">
            <QuickAction
              label={dashboardCopy.actionLabel}
              icon={<Plus size={16} />}
              onClick={() => navigate("/sales/new")}
              className={experience.kind === "general" ? "bg-white text-blue-700 hover:bg-blue-50" : "bg-white text-slate-900 hover:bg-white/90"}
            />
            <QuickAction
              label="Encaisser paiement"
              icon={<CreditCard size={16} />}
              onClick={() => navigate("/sales/collect")}
              className="bg-white/90 text-slate-800 hover:bg-white"
            />
          </div>

          {showDebtAlert && (
            <div className="relative mt-4 inline-flex items-center gap-2 rounded-xl border border-amber-200/50 bg-amber-400/20 px-3 py-2 text-sm text-amber-100">
              <AlertTriangle size={16} />
              Dette elevee: {totalDettes.toLocaleString()} FCFA
            </div>
          )}
        </section>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <Insight
            icon={carStore ? <CarFront size={18} /> : propertyStore ? <Building2 size={18} /> : <Package size={18} />}
            label={kpiLabels.stock}
            value={produitsEnStock}
            color={carStore ? "from-orange-500 to-amber-500" : propertyStore ? "from-emerald-500 to-teal-500" : "from-blue-600 to-cyan-600"}
            ring={carStore ? "ring-orange-100" : propertyStore ? "ring-emerald-100" : "ring-blue-100"}
          />
          <Insight
            icon={<ShoppingCart size={18} />}
            label={kpiLabels.sold}
            value={produitsVendus}
            color={carStore ? "from-slate-900 to-orange-700" : propertyStore ? "from-teal-700 to-cyan-600" : "from-indigo-600 to-blue-700"}
            ring={carStore ? "ring-slate-200" : propertyStore ? "ring-teal-100" : "ring-indigo-100"}
          />
          <Insight
            icon={<Users size={18} />}
            label={kpiLabels.clients}
            value={totalClients}
            color={carStore ? "from-orange-600 to-red-500" : propertyStore ? "from-cyan-600 to-teal-500" : "from-violet-600 to-indigo-600"}
            ring={carStore ? "ring-orange-100" : propertyStore ? "ring-cyan-100" : "ring-violet-100"}
          />
          <Insight
            icon={<Wallet size={18} />}
            label={kpiLabels.cash}
            value={`${Number(totalEncaisse || 0).toLocaleString()} FCFA`}
            color="from-emerald-600 to-green-500"
            ring="ring-emerald-100"
          />
        </div>

        {catalogStore && (
          <section className={`rounded-3xl border p-5 sm:p-6 shadow-sm ${experience.stock.panelClass}`}>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <h2 className="text-lg font-semibold text-slate-800">{dashboardCopy.collectionTitle}</h2>
                <p className="mt-1 text-sm text-slate-500">{dashboardCopy.collectionSubtitle}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {generalStore && (
                  <span className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700">
                    Valeur visible: {visibleCatalogValue.toLocaleString()} FCFA
                  </span>
                )}
                <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${experience.stock.availableStatusClass}`}>
                  {previewItems.length} fiche{previewItems.length > 1 ? "s" : ""} visible{previewItems.length > 1 ? "s" : ""}
                </span>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-1 gap-4 xl:grid-cols-3">
              {previewItems.map((item) => (
                <LiveCatalogCard
                  key={item._id || item.identifier || item.name}
                  item={item}
                  generalStore={generalStore}
                  carStore={carStore}
                  propertyStore={propertyStore}
                  experience={experience}
                />
              ))}

              {previewItems.length === 0 && (
                <div className={`xl:col-span-3 rounded-2xl border border-dashed p-8 text-center text-slate-500 ${experience.stock.panelMutedClass}`}>
                  Aucun element catalogue a afficher pour le moment.
                </div>
              )}
            </div>
          </section>
        )}

        {generalStore && (
          <section className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <GeneralPulseCard
              title="Stock mobilisable"
              value={produitsEnStock}
              subtitle="References prêtes a convertir en vente"
              tone="from-sky-50 via-white to-cyan-50"
              icon={<Package size={18} />}
            />
            <GeneralPulseCard
              title="Clients actifs"
              value={totalClients}
              subtitle="Base client deja engagee avec la boutique"
              tone="from-indigo-50 via-white to-blue-50"
              icon={<Users size={18} />}
            />
            <GeneralPulseCard
              title="Encaissement cumule"
              value={`${Number(totalEncaisse || 0).toLocaleString()} FCFA`}
              subtitle="Lecture rapide du niveau de traction commercial"
              tone="from-emerald-50 via-white to-teal-50"
              icon={<Wallet size={18} />}
            />
          </section>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
          <div className={`${chartSpanClass} rounded-3xl border p-5 sm:p-6 shadow-sm ${experience.stock.panelClass}`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-800">{dashboardCopy.chartTitle}</h2>
              <span className={`text-xs rounded-full px-2.5 py-1 ${experience.stock.secondaryButtonClass}`}>{dashboardCopy.chartBadge}</span>
            </div>

            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={last7DaysSales}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#64748b" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748b" }} />
                <Tooltip
                  formatter={(v: any) => [`${Number(v || 0).toLocaleString()} FCFA`, "Montant"]}
                  labelFormatter={(label) => `Jour: ${label}`}
                />
                <Line
                  type="monotone"
                  dataKey="amount"
                  stroke={propertyStore ? "#0f766e" : carStore ? "#ea580c" : "#2563eb"}
                  strokeWidth={3}
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className={`${sideSpanClass} rounded-3xl border p-5 sm:p-6 shadow-sm ${experience.stock.panelClass}`}>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-slate-800">{dashboardCopy.debtTitle}</h2>
              <span className="text-xs rounded-full bg-rose-50 px-2.5 py-1 text-rose-700">{dashboardCopy.debtBadge}</span>
            </div>

            <div className="space-y-3">
              {debtors.map((d: any) => {
                const debtorName = d?.clientName || d?.name || "Client"
                const debtorPhone = d?.clientPhone || d?.phone || "-"
                const debtorDebt = Number(d?.remaining || d?.totalDebt || 0)
                const debtorProduct = d?.modelId?.name || d?.productId?.name || "-"

                return (
                  <div key={d?._id || `${debtorName}-${debtorPhone}`} className="rounded-2xl border border-slate-200 p-3 hover:bg-slate-50 transition">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="font-medium text-slate-800">{debtorName}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{debtorPhone}</p>
                        <p className="text-xs text-slate-400 mt-1">{debtorProduct}</p>
                      </div>
                      <span className="text-xs rounded-full bg-rose-50 px-2.5 py-1 font-semibold text-rose-700">
                        {debtorDebt.toLocaleString()} FCFA
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => navigate("/sales/collect", { state: { sale: d } })}
                      className="mt-3 inline-flex items-center rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-100"
                    >
                      Voir et encaisser
                    </button>
                  </div>
                )
              })}

              {debtors.length === 0 && (
                <p className="text-sm text-slate-500 py-8 text-center">Aucun debiteur actuellement</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <SubscriptionModal
        isOpen={openSubscriptionModal}
        currentPlan={currentPlan}
        currentBillingCycle={currentBillingCycle}
        plans={availablePlans}
        onClose={() => setOpenSubscriptionModal(false)}
        onUpgrade={(planKey, billingCycle) =>
          runSubscriptionUpgrade({
            planKey,
            billingCycle,
            currentPlan,
            currentBillingCycle,
            user,
            dispatch,
            onClose: () => setOpenSubscriptionModal(false),
          })
        }
      />
    </>
  )
}

function QuickAction({
  label,
  icon,
  onClick,
  className,
}: {
  label: string
  icon: React.ReactNode
  onClick: () => void
  className: string
}) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition ${className}`}
    >
      {icon}
      {label}
    </button>
  )
}

function Insight({
  icon,
  label,
  value,
  color,
  ring,
}: {
  icon: React.ReactNode
  label: string
  value: string | number
  color: string
  ring: string
}) {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ring-1 ${ring}`}>
      <div className="flex items-center gap-4">
        <div className={`h-11 w-11 rounded-xl bg-gradient-to-br ${color} text-white flex items-center justify-center shadow-lg`}>
          {icon}
        </div>
        <div>
          <p className="text-sm text-slate-500">{label}</p>
          <p className="text-xl font-bold text-slate-800">{value}</p>
        </div>
      </div>
    </div>
  )
}

function LiveCatalogCard({
  item,
  generalStore,
  carStore,
  propertyStore,
  experience,
}: {
  item: LiveCatalogItem
  generalStore: boolean
  carStore: boolean
  propertyStore: boolean
  experience: ReturnType<typeof getStoreExperience>
}) {
  const imageUrl = item.coverImageUrl || item.imageUrl || ""
  const availability = String(item.availability || item.status || "OUI").toUpperCase()
  const listingType = String(item.listingType || "SALE").toUpperCase()

  return (
    <div className={`overflow-hidden rounded-3xl border border-white/50 shadow-sm ${experience.stock.panelMutedClass}`}>
      <div className="relative h-48 overflow-hidden bg-slate-200">
        {imageUrl ? (
          <img src={imageUrl} alt={item.name || "catalog"} className="h-full w-full object-cover" />
        ) : (
          <div className={`flex h-full w-full items-center justify-center bg-gradient-to-br ${carStore ? "from-slate-900 via-slate-700 to-orange-500" : propertyStore ? "from-emerald-700 via-teal-600 to-cyan-500" : "from-sky-700 via-blue-700 to-cyan-500"} text-white`}>
            {carStore ? <CarFront size={40} /> : propertyStore ? <Building2 size={40} /> : <Package size={40} />}
          </div>
        )}

        <div className="absolute left-4 top-4 flex gap-2">
          <span className="rounded-full bg-white/90 px-2.5 py-1 text-xs font-semibold text-slate-900">
            {generalStore ? "Produit en vue" : listingType === "RENT" ? "Location" : listingType === "BOTH" ? "Vente & location" : "Vente"}
          </span>
          <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${availability === "NON" ? "bg-slate-900/80 text-white" : "bg-emerald-100 text-emerald-800"}`}>
            {availability === "NON" || availability === "OUT" || availability === "SOLD" ? "Indisponible" : "Disponible"}
          </span>
        </div>
      </div>

      <div className="space-y-4 p-4">
        <div>
          <p className="text-xs uppercase tracking-[0.16em] text-slate-400">
            {carStore ? "Vehicule live" : propertyStore ? "Bien live" : "Produit live"}
          </p>
          <h3 className="mt-2 text-lg font-semibold text-slate-900">{item.name || "Reference sans nom"}</h3>
          <p className="mt-1 text-sm text-slate-500">
            {generalStore ? item.brand || "Marque non renseignee" : item.identifier || "Reference non renseignee"}
          </p>
        </div>

        {carStore ? (
          <div className="grid grid-cols-2 gap-3 text-sm">
            <PreviewStat label="Annee" value={item.year || "—"} icon={<CalendarDays size={14} />} />
            <PreviewStat label="Kilometrage" value={item.mileage || "—"} icon={<Gauge size={14} />} />
          </div>
        ) : propertyStore ? (
          <div className="grid grid-cols-2 gap-3 text-sm">
            <PreviewStat label="Quartier" value={item.district || "—"} icon={<MapPin size={14} />} />
            <PreviewStat label="Type" value={item.propertyType || "—"} icon={<BedDouble size={14} />} />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 text-sm">
            <PreviewStat label="Reference" value={item.identifier || "—"} icon={<Package size={14} />} />
            <PreviewStat label="Quantite" value={String(item.quantity || 0)} icon={<Boxes size={14} />} />
          </div>
        )}

        {generalStore ? (
          <div className="grid grid-cols-2 gap-3">
            <PriceBlock label="Prix vente" value={item.sellPrice} />
            <PriceBlock label="Prix achat" value={item.buyPrice} />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <PriceBlock
              label={propertyStore ? "Prix vente" : carStore ? "Prix affiche" : "Prix"}
              value={item.sellPrice}
            />
            <PriceBlock
              label={propertyStore ? "Prix / mois" : "Prix / jour"}
              value={propertyStore ? item.monthlyRentalPrice : item.dailyRentalPrice}
            />
          </div>
        )}
      </div>
    </div>
  )
}

function PreviewStat({
  label,
  value,
  icon,
}: {
  label: string
  value: string
  icon: React.ReactNode
}) {
  return (
    <div className="rounded-2xl bg-white/80 p-3">
      <div className="flex items-center gap-2 text-slate-500">
        {icon}
        <p className="text-xs">{label}</p>
      </div>
      <p className="mt-1 text-sm font-semibold text-slate-900">{value}</p>
    </div>
  )
}

function PriceBlock({
  label,
  value,
}: {
  label: string
  value?: number
}) {
  return (
    <div className="rounded-2xl bg-white/85 p-3">
      <p className="text-xs text-slate-500">{label}</p>
      <p className="mt-1 text-sm font-semibold text-slate-900">
        {value ? `${Number(value).toLocaleString()} FCFA` : "—"}
      </p>
    </div>
  )
}

function HeroMiniStat({
  label,
  value,
  hint,
  icon,
}: {
  label: string
  value: string | number
  hint: string
  icon: React.ReactNode
}) {
  return (
    <div className="rounded-2xl border border-white/20 bg-white/10 p-4 backdrop-blur-sm">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.12em] text-white/70">{label}</p>
          <p className="mt-2 text-lg font-semibold text-white">{value}</p>
          <p className="mt-1 text-xs text-white/75">{hint}</p>
        </div>
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/15 text-white">
          {icon}
        </div>
      </div>
    </div>
  )
}

function GeneralPulseCard({
  title,
  value,
  subtitle,
  tone,
  icon,
}: {
  title: string
  value: string | number
  subtitle: string
  tone: string
  icon: React.ReactNode
}) {
  return (
    <div className={`rounded-3xl border border-slate-200 bg-gradient-to-br p-5 shadow-sm ${tone}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-sm font-medium text-slate-500">{title}</p>
          <p className="mt-2 text-2xl font-bold text-slate-900">{value}</p>
          <p className="mt-2 text-sm text-slate-600">{subtitle}</p>
        </div>
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-lg">
          {icon}
        </div>
      </div>
    </div>
  )
}
